import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, Save, RotateCcw, Clock, CheckCircle, AlertCircle, Info } from 'lucide-react';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const TYPE_ICONS = {
  confirmation_rdv: '📅',
  soumission: '📄',
  notification_interne: '🔔',
  production: '🏗️',
  facture: '💳',
  rappel_paiement: '⏰',
  devis: '📋',
};

export function EmailConfiguration() {
  const [config, setConfig] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [activeTab, setActiveTab] = useState('config');

  const token = localStorage.getItem('auth_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchConfig();
    fetchLogs();
  }, []);

  const fetchConfig = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/configurations/emails`, { headers });
      if (r.status !== 200) throw new Error();
      const data = r.data;
      setConfig(data.config || {});
      setSavedAt(data.updated_at);
    } catch {
      toast.error('Impossible de charger la configuration email');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/configurations/emails/historique?limit=40`, { headers });
      if (r.status !== 200) throw new Error();
      const data = r.data;
      setLogs(data.logs || []);
    } catch {
      // silently fail
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleChange = (emailType, field, value) => {
    setConfig(prev => ({
      ...prev,
      [emailType]: { ...prev[emailType], [field]: value },
    }));
    setIsDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/configurations/emails`, { config }, { headers });
      if (r.status !== 200) throw new Error();
      const data = r.data;
      setSavedAt(data.updated_at);
      setIsDirty(false);
      toast.success('Configuration email sauvegardée !');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('fr-CA', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="email-configuration-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Mail className="w-7 h-7 text-blue-600" />
            Configuration des Emails
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Gérez les adresses d'expédition et de réponse pour chaque type d'email.
            {isDirty && <span className="ml-2 text-amber-600 font-medium">• Modifications non sauvegardées</span>}
            {savedAt && !isDirty && (
              <span className="ml-2 text-green-600 text-xs">Dernière sauvegarde : {formatDate(savedAt)}</span>
            )}
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving || !isDirty}
          data-testid="save-email-config-btn"
          className={`text-white ${isDirty ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'config' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Configuration
        </button>
        <button
          onClick={() => setActiveTab('historique')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'historique' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-historique-emails"
        >
          Historique des envois
          {logs.length > 0 && <Badge className="ml-2 bg-slate-100 text-slate-600 text-xs">{logs.length}</Badge>}
        </button>
      </div>

      {activeTab === 'config' && (
        <div className="space-y-4">
          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Adresses autorisées Resend</p>
              <p>Utilisez uniquement des adresses du domaine <code className="bg-blue-100 px-1 rounded">@peintureresidentielleqc.com</code> — elles sont vérifiées dans Resend.</p>
            </div>
          </div>

          {/* Email type cards */}
          {Object.entries(config).map(([emailType, conf]) => (
            <Card key={emailType} data-testid={`email-config-card-${emailType}`}>
              <CardHeader className="pb-3 pt-4">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{TYPE_ICONS[emailType] || '📧'}</span>
                  <div>
                    <CardTitle className="text-base">{conf.label || emailType}</CardTitle>
                    <CardDescription className="text-xs mt-0.5">{conf.description}</CardDescription>
                  </div>
                  <div className="ml-auto text-right hidden sm:block">
                    <p className="text-xs text-slate-400 italic">{conf.declencheur}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Expéditeur (From)
                    </label>
                    <Input
                      value={conf.from_email || ''}
                      onChange={(e) => handleChange(emailType, 'from_email', e.target.value)}
                      placeholder="expediteur@peintureresidentielleqc.com"
                      className="h-9 text-sm"
                      data-testid={`input-from-${emailType}`}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Répondre à (Reply-To)
                    </label>
                    <Input
                      value={conf.reply_to || ''}
                      onChange={(e) => handleChange(emailType, 'reply_to', e.target.value)}
                      placeholder="reponse@peintureresidentielleqc.com"
                      className="h-9 text-sm"
                      data-testid={`input-reply-to-${emailType}`}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeTab === 'historique' && (
        <div className="space-y-3">
          {loadingLogs ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>Aucun email envoyé pour l'instant.</p>
            </div>
          ) : (
            logs.map((log, i) => (
              <div
                key={log.id || log._id || i}
                className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
                data-testid={`email-log-row-${i}`}
              >
                {log.status === 'envoye' ? (
                  <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-slate-800 truncate">{log.subject || '(sans sujet)'}</p>
                    <Badge className={log.status === 'envoye' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {log.status === 'envoye' ? 'Envoyé' : log.status === 'template_absent' ? 'Template manquant' : 'Erreur'}
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-xs">{log.template_code || log.context?.type}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    À : <span className="font-medium">{log.to}</span>
                    {log.error && <span className="ml-2 text-red-500">— {log.error}</span>}
                  </p>
                </div>
                <div className="text-xs text-slate-400 shrink-0 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(log.sent_at)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default EmailConfiguration;
